import { GsFormField, GsFormIfArg } from '../../../components';
import { generatePaginatedSchemaFields, ITEMS_PER_ROW_SCHEMA_FIELDS, PAGINATED_SCHEMA_FIELDS } from './paginated.schema';

export enum Mode {
  /** Display items in a grid */
  GRID = 0,
  /** Display items in a slider (could also be a grid) */
  SLIDER = 1
  // TODO: implement list layout
  /** Display items in a list */
  // LIST = 2,
}

const MODE_IF_ARG: GsFormIfArg = {
  formControlName: 'mode',
  value: Mode.SLIDER
};

const GRID_MODE_IF_ARG: GsFormIfArg = {
  formControlName: 'mode',
  value: Mode.GRID
};

export function generateSwiperSchemaFields(
  usePaginated: boolean = true,
  ifArgs: GsFormIfArg[] = [MODE_IF_ARG],
  includeInfiniteScroll: boolean = false
): GsFormField<never>[] {
  // Use the new generatePaginatedSchemaFields function to support INFINITE_SCROLL in GRID mode
  const paginatedFields = usePaginated
    ? includeInfiniteScroll
      ? generatePaginatedSchemaFields(true, [GRID_MODE_IF_ARG])
      : PAGINATED_SCHEMA_FIELDS
    : ITEMS_PER_ROW_SCHEMA_FIELDS;

  return [
    ...paginatedFields,
    {
      formControlName: 'sliderTeaseItem',
      label: 'schema.swiper.teaseItem',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.swiper.options',
      if: {
        args: ifArgs
      }
    },
    {
      formControlName: 'sliderTeaseItemSize',
      label: 'schema.swiper.teaseItemSize',
      type: 'select',
      valueType: 'number',
      default: 50,
      groupingName: 'schema.swiper.options',
      options: [
        { text: '10%', value: 10 },
        { text: '20%', value: 20 },
        { text: '30%', value: 30 },
        { text: '40%', value: 40 },
        { text: '50%', value: 50 },
        { text: '60%', value: 60 },
        { text: '70%', value: 70 },
        { text: '80%', value: 80 },
        { text: '90%', value: 90 }
      ],
      if: {
        chaining: 'and',
        args: [...ifArgs, { formControlName: 'sliderTeaseItem', value: true }]
      }
    },
    {
      formControlName: 'sliderAmountGroup',
      label: 'schema.swiper.amountPerClick',
      type: 'number',
      default: 1,
      min: 1,
      groupingName: 'schema.swiper.options',
      if: {
        args: ifArgs
      }
    },
    {
      formControlName: 'sliderNavigation',
      label: 'schema.swiper.addArrows',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.swiper.options',
      if: {
        args: ifArgs
      }
    },
    {
      formControlName: 'sliderAutoplay',
      label: 'schema.swiper.autoplay',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.swiper.options',
      if: {
        args: ifArgs
      }
    },
    {
      formControlName: 'sliderDelay',
      label: 'schema.swiper.delay',
      type: 'number',
      default: 4000,
      groupingName: 'schema.swiper.options',
      if: {
        args: [...ifArgs, { formControlName: 'sliderAutoplay', value: true }]
      }
    },
    {
      formControlName: 'sliderSpeed',
      label: 'schema.swiper.speed',
      type: 'number',
      default: 500,
      groupingName: 'schema.swiper.options',
      if: {
        args: [...ifArgs, { formControlName: 'sliderAutoplay', value: true }]
      }
    },
    {
      formControlName: 'sliderLoop',
      label: 'schema.swiper.loop',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.swiper.options',
      if: {
        args: ifArgs
      }
    },
    {
      formControlName: 'sliderPagination',
      label: 'schema.swiper.pagination',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.swiper.options',
      if: {
        chaining: 'and',
        args: [...ifArgs, { formControlName: 'sliderLoop', value: false }, { formControlName: 'sliderScrollbar', value: false }]
      }
    },
    {
      formControlName: 'sliderScrollbar',
      label: 'schema.swiper.scrollbar',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.swiper.options',
      if: {
        chaining: 'and',
        args: [...ifArgs, { formControlName: 'sliderLoop', value: false }, { formControlName: 'sliderPagination', value: false }]
      }
    },
    {
      formControlName: 'sliderSpaceBetween',
      label: 'schema.items.spaceBetween',
      type: 'number',
      default: 30,
      groupingName: 'schema.items.name'
    }
  ];
}

export const SWIPER_SCHEMA_FIELDS: GsFormField<never>[] = [...generateSwiperSchemaFields()];

export const SWIPER_WITH_GRID_SCHEMA_FIELDS: GsFormField<never>[] = [
  ...generateSwiperSchemaFields(true, [MODE_IF_ARG], true),
  {
    formControlName: 'itemPadding',
    label: 'schema.items.itemPadding',
    type: 'padding',
    useTailwindClasses: true,
    groupingName: 'schema.items.name',
    default: {
      top: 2,
      right: 2,
      bottom: 2,
      left: 2
    }
  },
  {
    formControlName: 'sliderColumns',
    label: 'schema.swiper.rowsOfItems',
    type: 'number',
    groupingName: 'schema.swiper.options',
    default: 1,
    min: 1,
    if: {
      args: [{ formControlName: 'mode', value: Mode.SLIDER }]
    }
  }
];
